import React from "react";
import { observer } from "mobx-react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import {
  RedemptionHow,
  RedemptionProductRow,
  RedemptionProductsHow,
} from "../../../../../../models/Dashboard/Redemption/RedemptionSummary";
import { RedemptionDashboardStore } from "../../../../stores/dashboards/RedemptionDashboardStore";
import {
  formatRedemptionCount,
  formatRedemptionShare,
  formatTopVariant,
} from "./redemptionFormat";
import { RedemptionEmptyState, RedemptionSectionLabel } from "./RedemptionSection";
import RedemptionHint from "./RedemptionHint";

interface RedemptionProductsTableProps {
  store: RedemptionDashboardStore;
}

const ShareBar = ({ sharePct }: { sharePct: number }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const width = Math.max(0, Math.min(100, sharePct));

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 90,
          height: 7,
          borderRadius: 999,
          overflow: "hidden",
          bgcolor: colors.greyBackground,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: `${width}%`,
            height: "100%",
            borderRadius: 999,
            background: `linear-gradient(90deg, ${colors.purple}, ${colors.purpleDark})`,
          }}
        />
      </Box>
      <CustomTypography variant="body3" color={colors.mutedSlateText}>
        {formatRedemptionShare(sharePct)}
      </CustomTypography>
    </Box>
  );
};

const headerCellSx = {
  fontSize: "0.68rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  whiteSpace: "nowrap" as const,
  borderBottom: "1px solid",
  py: 1.1,
};

const HeaderLabel = ({
  label,
  how,
}: {
  label: string;
  how?: RedemptionHow | null;
}) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      justifyContent: "inherit",
    }}
  >
    <span>{label}</span>
    <RedemptionHint how={how} fontSize={12} />
  </Box>
);

const COLUMN_HOW_KEYS: Array<{
  label: string;
  align: "left" | "right";
  howKey?: keyof RedemptionProductsHow;
}> = [
  { label: "Product ID", align: "left" },
  { label: "Product", align: "left" },
  { label: "Units", align: "right", howKey: "units" },
  { label: "Share", align: "left", howKey: "share_pct" },
  { label: "Coupons", align: "right", howKey: "coupons" },
  { label: "Top variant", align: "left", howKey: "top_variant" },
  { label: "Top city", align: "left", howKey: "top_city" },
];

const RedemptionProductsTable: React.FC<RedemptionProductsTableProps> = ({
  store,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const productsBlock = store.summaryResult?.data.products;
  const rows = productsBlock?.rows ?? [];
  const how = productsBlock?.how;

  if (!store.summaryResult || store.summaryError) {
    return null;
  }

  const openProduct = (row: RedemptionProductRow) => {
    try {
      void store.openProduct(row.product_id, row.product_name);
    } catch (error) {
      console.error(error);
    }
  };

  const openMatrix = () => {
    try {
      void store.openMatrix();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box>
      <RedemptionSectionLabel
        label="Products redeemed"
        how={how?.units}
        action={
          <AppButton
            label="Product · variant · count"
            variant="transparentPrimary"
            startIcon={<AccountTreeOutlinedIcon />}
            onClick={openMatrix}
            customStyles={{ minHeight: 32, height: 32, px: 1.25 }}
          />
        }
      />
      {rows.length > 0 ? (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderColor: colors.lightGray,
            overflow: "hidden",
          }}
        >
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {COLUMN_HOW_KEYS.map((column) => (
                    <TableCell
                      key={column.label}
                      align={column.align}
                      sx={{
                        ...headerCellSx,
                        color: colors.greyTextColor,
                        bgcolor: colors.white,
                        borderColor: colors.lightGray,
                      }}
                    >
                      <HeaderLabel
                        label={column.label}
                        how={column.howKey ? how?.[column.howKey] : null}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.product_id}
                    hover
                    onClick={() => openProduct(row)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openProduct(row);
                      }
                    }}
                    tabIndex={0}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ borderColor: colors.lightGray }}>
                      <CustomTypography
                        variant="body2"
                        fontWeight={600}
                        color={colors.navyText}
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {row.product_id}
                      </CustomTypography>
                    </TableCell>
                    <TableCell sx={{ borderColor: colors.lightGray }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1,
                            display: "grid",
                            placeItems: "center",
                            bgcolor: colors.veryLightGray,
                            color: colors.greyTextColor,
                            flexShrink: 0,
                          }}
                        >
                          <Inventory2OutlinedIcon sx={{ fontSize: 17 }} />
                        </Box>
                        <CustomTypography
                          variant="body2"
                          fontWeight={600}
                          color={colors.navyText}
                        >
                          {row.product_name}
                        </CustomTypography>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ borderColor: colors.lightGray }}
                    >
                      <CustomTypography
                        variant="body2"
                        fontWeight={600}
                        color={colors.navyText}
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {formatRedemptionCount(row.units)}
                      </CustomTypography>
                    </TableCell>
                    <TableCell sx={{ borderColor: colors.lightGray }}>
                      <ShareBar sharePct={row.share_pct} />
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ borderColor: colors.lightGray }}
                    >
                      <CustomTypography
                        variant="body2"
                        fontWeight={600}
                        color={colors.navyText}
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {formatRedemptionCount(row.coupons)}
                      </CustomTypography>
                    </TableCell>
                    <TableCell sx={{ borderColor: colors.lightGray }}>
                      <CustomTypography variant="body2" color={colors.text}>
                        {formatTopVariant(
                          row.top_variant,
                          row.top_variant_units,
                        )}
                      </CustomTypography>
                    </TableCell>
                    <TableCell sx={{ borderColor: colors.lightGray }}>
                      <CustomTypography variant="body2" color={colors.text}>
                        {row.top_city || "—"}
                      </CustomTypography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <RedemptionEmptyState message="No products redeemed for this filter." />
      )}
    </Box>
  );
};

export default observer(RedemptionProductsTable);
